// The charts the Usage screens draw, as plain SVG: the active-against-bought
// trend, the consumption burn-down, and unused capacity by vendor. Each mark
// carries a title for hover.

import { FC, MouseEvent, useRef, useState } from "react";
import { clsx } from "clsx";
import { int, money, monthShort } from "../model/format";
import type { ConsumptionMonth, TrendPoint } from "../model/types";
import classes from "./charts.module.less";

const RADIUS = 4;

// ---- Trend ---------------------------------------------------------------

/** Active users against licenses bought by month, the projection dashed. */
export const TrendChart: FC<{ points: TrendPoint[]; height?: number }> = ({
    points,
    height = 200,
}) => {
    const width = 720;
    const margin = { top: 16, right: 56, bottom: 28, left: 44 };
    const plotW = width - margin.left - margin.right;
    const plotH = height - margin.top - margin.bottom;
    const values = points.flatMap((p) => [p.bought, p.active]);
    const max = Math.max(...values) * 1.05;
    const min = Math.min(...values) * 0.85;
    const x = (i: number) => margin.left + (i / (points.length - 1)) * plotW;
    const y = (v: number) => margin.top + plotH - ((v - min) / (max - min)) * plotH;
    const path = (pick: (p: TrendPoint) => number, subset: TrendPoint[], offset: number) =>
        subset.map((p, i) => `${i === 0 ? "M" : "L"}${x(offset + i)},${y(pick(p))}`).join(" ");

    const actualCount = points.filter((p) => !p.projected).length;
    const actual = points.slice(0, actualCount);
    // The projection starts from the last actual point so the line is continuous.
    const projected = points.slice(Math.max(0, actualCount - 1));
    const ticks = [min, (min + max) / 2, max].map((v) => Math.round(v / 100) * 100);
    const last = points[points.length - 1];
    const lastActual = actual[actual.length - 1];

    // Hover: the nearest month, a guide line through it, both series' values there.
    const [hover, setHover] = useState<number>();
    const svgRef = useRef<SVGSVGElement>(null);
    const onMove = (e: MouseEvent<SVGSVGElement>) => {
        const rect = svgRef.current?.getBoundingClientRect();
        if (!rect) return;
        const px = ((e.clientX - rect.left) / rect.width) * width;
        setHover(
            Math.min(
                points.length - 1,
                Math.max(0, Math.round(((px - margin.left) / plotW) * (points.length - 1)))
            )
        );
    };
    const hovered = hover == null ? undefined : points[hover];
    const tooltipLines = hovered
        ? [
              `${monthShort(hovered.month)} ${hovered.month.slice(0, 4)}${hovered.projected ? ", projected" : ""}`,
              `Active users: ${int(hovered.active)}`,
              `Licenses bought: ${int(hovered.bought)}`,
          ]
        : [];
    const tooltipW = 150;
    const tooltipX =
        hover != null && x(hover) + 12 + tooltipW > width
            ? x(hover) - 12 - tooltipW
            : x(hover ?? 0) + 12;

    return (
        <div>
            <svg
                ref={svgRef}
                className={classes.chart}
                viewBox={`0 0 ${width} ${height}`}
                role="img"
                aria-label="Active users against licenses bought"
                onMouseMove={onMove}
                onMouseLeave={() => setHover(undefined)}
            >
                {ticks.map((t) => (
                    <g key={t}>
                        <line
                            className={classes.grid}
                            x1={margin.left}
                            x2={width - margin.right}
                            y1={y(t)}
                            y2={y(t)}
                        />
                        <text
                            className={classes.label}
                            x={margin.left - 6}
                            y={y(t) + 4}
                            textAnchor="end"
                        >
                            {int(t)}
                        </text>
                    </g>
                ))}
                <path
                    className={clsx(classes.line, classes["line--bought"])}
                    d={path((p) => p.bought, actual, 0)}
                />
                <path
                    className={clsx(
                        classes.line,
                        classes["line--bought"],
                        classes["line--projected"]
                    )}
                    d={path((p) => p.bought, projected, actualCount - 1)}
                />
                <path className={classes.line} d={path((p) => p.active, actual, 0)} />
                <path
                    className={clsx(classes.line, classes["line--projected"])}
                    d={path((p) => p.active, projected, actualCount - 1)}
                />
                {points.map((p, i) => (
                    <g key={p.month}>
                        {(i === actualCount - 1 || i === points.length - 1) && (
                            <>
                                <circle
                                    className={classes.marker}
                                    cx={x(i)}
                                    cy={y(p.active)}
                                    r={4}
                                />
                                <circle
                                    className={clsx(classes.marker, classes["marker--bought"])}
                                    cx={x(i)}
                                    cy={y(p.bought)}
                                    r={4}
                                />
                            </>
                        )}
                        {(i % 3 === 0 || i === points.length - 1) && (
                            <text
                                className={classes.label}
                                x={x(i)}
                                y={height - 8}
                                textAnchor="middle"
                            >
                                {monthShort(p.month)}
                                {p.month.endsWith("-01") ? ` ${p.month.slice(2, 4)}` : ""}
                            </text>
                        )}
                    </g>
                ))}
                <text className={classes.value} x={x(points.length - 1) + 8} y={y(last.active) + 4}>
                    {int(last.active)}
                </text>
                <text className={classes.value} x={x(points.length - 1) + 8} y={y(last.bought) + 4}>
                    {int(last.bought)}
                </text>
                <text
                    className={classes.label}
                    x={x(actualCount - 1)}
                    y={margin.top - 4}
                    textAnchor="middle"
                >
                    {int(lastActual.active)} active today
                </text>
                {hover != null && hovered && (
                    <g>
                        <line
                            className={classes.guide}
                            x1={x(hover)}
                            x2={x(hover)}
                            y1={margin.top}
                            y2={margin.top + plotH}
                        />
                        <circle
                            className={classes.marker}
                            cx={x(hover)}
                            cy={y(hovered.active)}
                            r={4}
                        />
                        <circle
                            className={clsx(classes.marker, classes["marker--bought"])}
                            cx={x(hover)}
                            cy={y(hovered.bought)}
                            r={4}
                        />
                        <g transform={`translate(${tooltipX}, ${margin.top + 4})`}>
                            <rect
                                className={classes.tooltip}
                                width={tooltipW}
                                height={tooltipLines.length * 14 + 10}
                                rx={4}
                            />
                            {tooltipLines.map((line, i) => (
                                <text
                                    key={line}
                                    className={clsx(
                                        classes.tooltip__text,
                                        i === 0 && classes["tooltip__text--title"]
                                    )}
                                    x={8}
                                    y={16 + i * 14}
                                >
                                    {line}
                                </text>
                            ))}
                        </g>
                    </g>
                )}
            </svg>
            <div className={classes.legend}>
                <span className={classes.legend__item}>
                    <span className={classes.legend__swatch} /> Active users (90 days)
                </span>
                <span className={classes.legend__item}>
                    <span
                        className={clsx(classes.legend__swatch, classes["legend__swatch--bought"])}
                    />{" "}
                    Licenses bought
                </span>
                <span className={classes.legend__item}>
                    <span
                        className={clsx(
                            classes.legend__swatch,
                            classes["legend__swatch--projected"]
                        )}
                    />{" "}
                    Projected, next term at 10,400
                </span>
            </div>
        </div>
    );
};

// ---- Horizontal bars -----------------------------------------------------

/** Amounts by label, largest first, as horizontal bars. */
export const HBarChart: FC<{ items: { label: string; amount: number }[]; rowHeight?: number }> = ({
    items,
    rowHeight = 22,
}) => {
    const width = 720;
    const labelW = 160;
    const valueW = 56;
    const barX = labelW + 8;
    const barW = width - barX - valueW - 8;
    const max = Math.max(...items.map((i) => i.amount));
    const height = items.length * rowHeight;
    return (
        <svg
            className={classes.chart}
            viewBox={`0 0 ${width} ${height}`}
            role="img"
            aria-label="Unused capacity by vendor"
        >
            {items.map((item, i) => {
                const yTop = i * rowHeight + 4;
                const h = rowHeight - 8;
                const w = Math.max(2, (item.amount / max) * barW);
                return (
                    <g key={item.label}>
                        <text
                            className={classes.hbar__label}
                            x={labelW}
                            y={yTop + h - 3}
                            textAnchor="end"
                        >
                            {item.label}
                        </text>
                        <rect
                            className={classes.hbar__track}
                            x={barX}
                            y={yTop}
                            width={barW}
                            height={h}
                            rx={RADIUS}
                        />
                        <rect
                            className={classes.bar}
                            x={barX}
                            y={yTop}
                            width={w}
                            height={h}
                            rx={RADIUS}
                        >
                            <title>{`${item.label}: ${money(item.amount)} a year`}</title>
                        </rect>
                        <text className={classes.hbar__value} x={barX + barW + 8} y={yTop + h - 3}>
                            {money(item.amount)}
                        </text>
                    </g>
                );
            })}
        </svg>
    );
};

// ---- Burn-down -----------------------------------------------------------

/** "2026-04" plus a number of months. */
const addMonths = (month: string, n: number): string => {
    const [y, m] = month.split("-").map(Number);
    const d = new Date(Date.UTC(y, m - 1 + n, 1));
    return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}`;
};

/**
 * Cumulative consumption against a straight-line commitment over the
 * commitment year, the forecast at the current run-rate dashed from today,
 * and the month the commitment is reached marked.
 */
export const BurnDownChart: FC<{
    monthly: ConsumptionMonth[];
    commitment: number;
    /** The commitment year's first and last month, "YYYY-MM". */
    from: string;
    to: string;
    /** USD a year, the last 90 days' pace. */
    runRate: number;
    reachedMonth?: string;
    height?: number;
}> = ({ monthly, commitment, from, to, runRate, reachedMonth, height = 230 }) => {
    const width = 720;
    const margin = { top: 28, right: 64, bottom: 28, left: 52 };
    const plotW = width - margin.left - margin.right;
    const plotH = height - margin.top - margin.bottom;
    const months: string[] = [];
    for (let m = from; m <= to; m = addMonths(m, 1)) months.push(m);
    const index = (month: string) => months.indexOf(month);
    const last = monthly[monthly.length - 1];
    // The forecast runs from the last actual month to the year's end at the run-rate.
    const forecast = months
        .slice(index(last.month))
        .map((month, i) => ({ month, value: last.consumed + (runRate / 12) * i }));
    const max = Math.max(commitment, forecast[forecast.length - 1].value) * 1.08;
    const x = (i: number) => margin.left + (i / (months.length - 1)) * plotW;
    const y = (v: number) => margin.top + plotH - (v / max) * plotH;
    const path = (points: { month: string; value: number }[]) =>
        points.map((p, i) => `${i === 0 ? "M" : "L"}${x(index(p.month))},${y(p.value)}`).join(" ");
    const ticks = [0, 0.25, 0.5, 0.75, 1].map((f) => f * max);
    const commitmentAt = (i: number) => (commitment * i) / (months.length - 1);

    // Hover: the nearest month, a guide line through it, the series' values there.
    const [hover, setHover] = useState<number>();
    const svgRef = useRef<SVGSVGElement>(null);
    const onMove = (e: MouseEvent<SVGSVGElement>) => {
        const rect = svgRef.current?.getBoundingClientRect();
        if (!rect) return;
        const px = ((e.clientX - rect.left) / rect.width) * width;
        const i = Math.round(((px - margin.left) / plotW) * (months.length - 1));
        setHover(Math.min(months.length - 1, Math.max(0, i)));
    };
    const hovered =
        hover == null
            ? undefined
            : {
                  month: months[hover],
                  consumed: monthly.find((m) => m.month === months[hover])?.consumed,
                  forecast: forecast.find((f) => f.month === months[hover])?.value,
                  commitment: commitmentAt(hover),
              };
    const tooltipLines = hovered
        ? [
              `${monthShort(hovered.month)} ${hovered.month.slice(0, 4)}`,
              hovered.consumed != null ? `Consumed: ${money(hovered.consumed)}` : undefined,
              hovered.forecast != null && hovered.consumed == null
                  ? `Forecast: ${money(hovered.forecast)}`
                  : undefined,
              `Commitment line: ${money(hovered.commitment)}`,
          ].filter((l): l is string => !!l)
        : [];
    const tooltipW = 150;
    const tooltipX =
        hover != null && x(hover) + 12 + tooltipW > width
            ? x(hover) - 12 - tooltipW
            : x(hover ?? 0) + 12;

    return (
        <div>
            <svg
                ref={svgRef}
                className={classes.chart}
                viewBox={`0 0 ${width} ${height}`}
                role="img"
                aria-label="Consumption against the commitment"
                onMouseMove={onMove}
                onMouseLeave={() => setHover(undefined)}
            >
                {ticks.map((t) => (
                    <g key={t}>
                        <line
                            className={classes.grid}
                            x1={margin.left}
                            x2={width - margin.right}
                            y1={y(t)}
                            y2={y(t)}
                        />
                        <text
                            className={classes.label}
                            x={margin.left - 6}
                            y={y(t) + 4}
                            textAnchor="end"
                        >
                            {money(t)}
                        </text>
                    </g>
                ))}
                {reachedMonth && index(reachedMonth) >= 0 && (
                    // The month the commitment is reached, with a month's margin either side; the note above the plot.
                    <g>
                        <rect
                            className={classes.band}
                            x={x(Math.max(0, index(reachedMonth) - 1))}
                            y={margin.top}
                            width={
                                x(Math.min(months.length - 1, index(reachedMonth) + 1)) -
                                x(Math.max(0, index(reachedMonth) - 1))
                            }
                            height={plotH}
                        />
                        <text
                            className={classes["label--alert"]}
                            x={x(index(reachedMonth))}
                            y={margin.top - 8}
                            textAnchor="middle"
                        >
                            Commitment reached
                        </text>
                    </g>
                )}
                <path
                    className={clsx(classes.line, classes["line--bought"])}
                    d={`M${x(0)},${y(0)} L${x(months.length - 1)},${y(commitment)}`}
                />
                <path
                    className={classes.line}
                    d={path(monthly.map((m) => ({ month: m.month, value: m.consumed })))}
                />
                <path
                    className={clsx(classes.line, classes["line--projected"])}
                    d={path(forecast)}
                />
                {months.map((month, i) => (
                    <g key={month}>
                        {(i % 3 === 0 || i === months.length - 1) && (
                            <text
                                className={classes.label}
                                x={x(i)}
                                y={height - 8}
                                textAnchor="middle"
                            >
                                {monthShort(month)}
                                {month.endsWith("-01") || i === 0 ? ` ${month.slice(2, 4)}` : ""}
                            </text>
                        )}
                    </g>
                ))}
                <circle
                    className={classes.marker}
                    cx={x(index(last.month))}
                    cy={y(last.consumed)}
                    r={4}
                />
                <text
                    className={classes.value}
                    x={x(index(last.month)) + 8}
                    y={y(last.consumed) - 8}
                >
                    {money(last.consumed)} to date
                </text>
                <text
                    className={classes.value}
                    x={x(months.length - 1) + 8}
                    y={y(forecast[forecast.length - 1].value) + 4}
                >
                    {money(forecast[forecast.length - 1].value)}
                </text>
                <text className={classes.label} x={x(months.length - 1) + 8} y={y(commitment) + 4}>
                    {money(commitment)}
                </text>
                {hover != null && hovered && (
                    <g>
                        <line
                            className={classes.guide}
                            x1={x(hover)}
                            x2={x(hover)}
                            y1={margin.top}
                            y2={margin.top + plotH}
                        />
                        <circle
                            className={clsx(classes.marker, classes["marker--bought"])}
                            cx={x(hover)}
                            cy={y(hovered.commitment)}
                            r={4}
                        />
                        {hovered.consumed != null && (
                            <circle
                                className={classes.marker}
                                cx={x(hover)}
                                cy={y(hovered.consumed)}
                                r={4}
                            />
                        )}
                        {hovered.consumed == null && hovered.forecast != null && (
                            <circle
                                className={classes.marker}
                                cx={x(hover)}
                                cy={y(hovered.forecast)}
                                r={4}
                            />
                        )}
                        <g transform={`translate(${tooltipX}, ${margin.top + 4})`}>
                            <rect
                                className={classes.tooltip}
                                width={tooltipW}
                                height={tooltipLines.length * 14 + 10}
                                rx={4}
                            />
                            {tooltipLines.map((line, i) => (
                                <text
                                    key={line}
                                    className={clsx(
                                        classes.tooltip__text,
                                        i === 0 && classes["tooltip__text--title"]
                                    )}
                                    x={8}
                                    y={16 + i * 14}
                                >
                                    {line}
                                </text>
                            ))}
                        </g>
                    </g>
                )}
            </svg>
            <div className={classes.legend}>
                <span className={classes.legend__item}>
                    <span className={classes.legend__swatch} /> Consumed
                </span>
                <span className={classes.legend__item}>
                    <span
                        className={clsx(
                            classes.legend__swatch,
                            classes["legend__swatch--projected"]
                        )}
                    />{" "}
                    Forecast at the last 90 days' pace
                </span>
                <span className={classes.legend__item}>
                    <span
                        className={clsx(classes.legend__swatch, classes["legend__swatch--bought"])}
                    />{" "}
                    Straight-line commitment
                </span>
            </div>
        </div>
    );
};
